import { ChevronLeft, ChevronRight, ArrowUpRight } from "lucide-react";
import {
  beijingToday,
  useRecordInsights,
  type ReviewPeriod,
} from "./useRecordInsights";
import "./record-insights.css";
export interface ReviewFilter {
  from: string;
  to: string;
  type?: string;
  dimension?: string;
}
const difference = (current: number, previous: number, unit: string) => {
  const delta = current - previous;
  return delta === 0 ? "持平" : `${delta > 0 ? "+" : ""}${delta} ${unit}`;
};
const shortDay = (value: string) => value.slice(5).replace("-", "/");
function Sparkline({ values, max }: { values: number[]; max: number }) {
  const points = values.map(
    (value, index) =>
      `${4 + (index * 202) / Math.max(1, values.length - 1)},${48 - (value / max) * 42}`,
  );
  return (
    <svg viewBox="0 0 210 54" aria-hidden="true" focusable="false">
      <path d="M4 48H206" stroke="var(--rc-line)" fill="none" />
      <polyline
        points={points.join(" ")}
        fill="none"
        stroke="var(--rc-accent)"
        strokeWidth="2"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      {points.map((point, index) => {
        const [cx, cy] = point.split(",");
        return (
          <circle key={index} cx={cx} cy={cy} r={2.5} fill="var(--rc-accent)">
            <title>{values[index]} 条相关记录</title>
          </circle>
        );
      })}
    </svg>
  );
}
export default function RecordInsights({
  period,
  anchor,
  onPeriod,
  onAnchor,
  onViewRecords,
  onCompose,
}: {
  period: ReviewPeriod;
  anchor: string;
  onPeriod: (value: ReviewPeriod) => void;
  onAnchor: (value: string) => void;
  onViewRecords: (filters: ReviewFilter) => void;
  onCompose: () => void;
}) {
  const { data, loading, error, reload } = useRecordInsights(period, anchor);
  const unit = period === "week" ? "周" : "月";
  const comparison = `较上${unit}${data?.comparison_is_shorter ? "整期" : data?.partial ? "同期" : ""}`;
  const view = (extra: Partial<ReviewFilter> = {}) =>
    data &&
    onViewRecords({
      from: data.selection.start,
      to: data.selection.through,
      ...extra,
    });
  const max = Math.max(
    1,
    ...(data?.trend.flatMap((point) => Object.values(point.dimensions)) || [1]),
  );
  return (
    <section className="record-insights" aria-label="周月记录汇总">
      <div className="ri-controls">
        <div className="ri-period" aria-label="汇总周期">
          <button
            aria-pressed={period === "week"}
            onClick={() => onPeriod("week")}
          >
            按周
          </button>
          <button
            aria-pressed={period === "month"}
            onClick={() => onPeriod("month")}
          >
            按月
          </button>
        </div>
        <label className="ri-date">
          回顾日期
          <input
            type="date"
            value={anchor}
            max={data?.today || beijingToday()}
            onChange={(event) =>
              event.target.value && onAnchor(event.target.value)
            }
          />
        </label>
        <div className="ri-paging">
          <button
            aria-label={`上一${unit}`}
            disabled={!data || loading}
            onClick={() => data && onAnchor(data.selection.previous_anchor)}
          >
            <ChevronLeft size={18} />
          </button>
          <button
            disabled={loading || data?.selection.is_current}
            onClick={() => onAnchor(data?.today || beijingToday())}
          >
            本{unit}
          </button>
          <button
            aria-label={`下一${unit}`}
            disabled={!data?.selection.next_anchor || loading}
            onClick={() =>
              data?.selection.next_anchor &&
              onAnchor(data.selection.next_anchor)
            }
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>
      {loading && (
        <p className="ri-state" role="status">
          正在汇总这一{unit}的记录…
        </p>
      )}
      {error && (
        <div className="rc-error" role="alert">
          {error}
          <button onClick={reload}>重试汇总</button>
        </div>
      )}
      {data && (
        <>
          <div className="ri-range">
            <div>
              <h2>{period === "week" ? "这一周" : "这个月"}，留下了什么</h2>
              <p>
                {data.selection.start} — {data.selection.end}
                {data.partial ? ` · 统计至 ${data.selection.through}` : ""}
              </p>
            </div>
            <button className="rc-text" onClick={() => view()}>
              查看本期记录 <ArrowUpRight size={16} />
            </button>
          </div>
          <div className="ri-totals">
            {(
              [
                ["记录总数", "total", "条"],
                ["记录天数", "active_days", "天"],
                ["覆盖维度", "covered_dimensions", "维"],
              ] as const
            ).map(([label, key, suffix]) => (
              <div key={key}>
                <span>{label}</span>
                <strong>
                  {data.current[key]} <small>{suffix}</small>
                </strong>
                <p>
                  {comparison}{" "}
                  {difference(data.current[key], data.comparison[key], suffix)}
                </p>
              </div>
            ))}
          </div>
          <p className="ri-caption">
            对照 {data.comparison.start} — {data.comparison.through}
            {data.partial
              ? data.comparison_is_shorter
                ? "，上期较短，使用上期完整周期。"
                : "，按相同天数比较。"
              : "。"}
            按北京时间统计。
          </p>
          {!data.current.total && (
            <div className="ri-empty">
              <p>这一{unit}还没有记录。留下一笔，让变化有迹可循。</p>
              <button className="rc-primary" onClick={onCompose}>
                随手记一笔
              </button>
            </div>
          )}
          <section className="ri-trends" aria-label="六维成长趋势">
            <div className="ri-section-heading">
              <h2>六维成长趋势</h2>
              <span>
                最近 {data.trend.length} {unit}
              </span>
            </div>
            <p className="ri-caption">
              以相关记录数量观察投入变化，不作为能力评分。一条笔记可以关联多个维度。
            </p>
            <div className="ri-dimensions">
              {Object.entries(data.dimension_labels).map(([key, label]) => (
                <button
                  key={key}
                  className="ri-dimension"
                  onClick={() => view({ dimension: key })}
                  aria-label={`查看${label}相关记录，本期${data.current.dimensions[key]}条`}
                >
                  <span className="ri-dimension-title">
                    {label}
                    <ArrowUpRight size={14} />
                  </span>
                  <span className="ri-dimension-number">
                    {data.current.dimensions[key]} <small>条</small>
                  </span>
                  <span className="ri-delta">
                    {comparison}{" "}
                    {difference(
                      data.current.dimensions[key],
                      data.comparison.dimensions[key],
                      "条",
                    )}
                  </span>
                  <Sparkline
                    values={data.trend.map((point) => point.dimensions[key])}
                    max={max}
                  />
                  <span className="ri-spark-range">
                    <span>
                      {period === "week"
                        ? shortDay(data.trend[0].start)
                        : data.trend[0].start.slice(0, 7)}
                    </span>
                    <span>
                      {period === "week"
                        ? shortDay(data.selection.start)
                        : data.selection.start.slice(0, 7)}
                    </span>
                  </span>
                </button>
              ))}
            </div>
            <details className="ri-detail">
              <summary>趋势明细</summary>
              <p className="ri-caption">
                各图使用相同刻度。0 表示该周期没有相关记录，不代表能力下降。
              </p>
              <div
                className="ri-table-scroll"
                tabIndex={0}
                role="region"
                aria-label="六维趋势明细表"
              >
                <table>
                  <caption>每{unit}相关记录数，点击周期查看记录</caption>
                  <thead>
                    <tr>
                      <th scope="col">周期</th>
                      {Object.values(data.dimension_labels).map((label) => (
                        <th scope="col" key={label}>
                          {label.slice(0, 1)}
                        </th>
                      ))}
                      <th scope="col">总记录</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.trend.map((point) => (
                      <tr key={point.start}>
                        <th scope="row">
                          <button
                            className="rc-text"
                            onClick={() =>
                              onViewRecords({
                                from: point.start,
                                to: point.through,
                              })
                            }
                          >
                            {period === "week"
                              ? `${shortDay(point.start)} — ${shortDay(point.through)}`
                              : point.start.slice(0, 7)}
                          </button>
                        </th>
                        {Object.keys(data.dimension_labels).map((key) => (
                          <td key={key}>{point.dimensions[key]}</td>
                        ))}
                        <td>{point.total}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          </section>
          <section className="ri-breakdown" aria-label="记录构成">
            <h2>记录构成</h2>
            <div className="ri-type-grid">
              {Object.entries(data.type_labels).map(([key, label]) => (
                <button key={key} onClick={() => view({ type: key })}>
                  <span>{label}</span>
                  <strong>
                    {data.current.by_type[key]} <small>条</small>
                  </strong>
                  <ArrowUpRight size={15} />
                </button>
              ))}
            </div>
          </section>
          <div className="ri-metrics">
            <span>
              运动时长 <strong>{data.current.exercise_minutes}</strong> 分钟
            </span>
            <span>
              学习事件 <strong>{data.current.study_events}</strong> 条
            </span>
            <span>
              饮水累计 <strong>{data.current.water_cups}</strong> 杯{" "}
              <small>（{data.current.water_cups * 250} ml）</small>
            </span>
          </div>
        </>
      )}
    </section>
  );
}
